import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

// Read-only WMS views for the client portal (Warehouse menu), plus their CSV
// downloads. The backend pins
// every call to the signed-in client (`provider_id`) — any `clientID` we send
// would be ignored — and lists every hub at once, so no hub picker here.
// There are deliberately no create / update / cancel calls in this module.

const CHANNEL_HEADERS_LIVE = {
  'X-Channel': 'LINK',
  'X-Api-Mode': 'live',
} as const;

const BASE = '/v1/warehouses';

export interface HubRef {
  id: number;
  name: string;
  code?: string | null;
}

export interface ClientRef {
  id: number;
  name: string;
}

export interface DocumentRef {
  id: string;
  code: string;
}

export interface Pagination {
  total: number;
  size: number;
  page: number;
  lastPage: number;
}

export interface Paged<T> {
  data: T[];
  pagination: Pagination;
}

export interface StatusCount<S extends string> {
  total: number;
  byStatus: { status: S; count: number }[];
}

/* -------------------------------- stock ------------------------------- */

export interface StockLevel {
  id: string;
  name: string;
}

export interface WarehouseStock {
  id: string;
  storageID: string;
  storageName: string;
  storage?: { id: string; name: string; category: string } | null;
  location?: StockLevel;
  section?: StockLevel;
  hubID: number;
  hub: HubRef | null;
  clientID: number;
  sku: string;
  itemName: string;
  uomName: string;
  batchNumber: string;
  expiredDate: string | null;
  qtyOnHand: number;
  qtyReserved: number;
  qtyAvailable: number;
}

export interface StockSummary {
  skuCount: number;
  binsInUse: number;
  qtyOnHand: number;
  qtyReserved: number;
  qtyAvailable: number;
}

export type StockMovementType =
  'INBOUND' | 'OUTBOUND' | 'TRANSFER' | 'ADJUSTMENT';

/** One stock ledger row ("Riwayat pergerakan stok"), newest first. */
export interface StockMovement {
  id: string;
  hubID: number;
  hub: HubRef | null;
  sku: string;
  batchNumber: string;
  type: StockMovementType;
  refType: string;
  refCode: string;
  /** Name path of the source storage; null when stock came in from outside. */
  storageFromName: string | null;
  /** Name path of the destination storage; null when stock left the hub. */
  storageToName: string | null;
  qtyDelta: number;
  createdAt: string;
}

/* --------------------------------- SKU -------------------------------- */

export type SkuStatus = 'ACTIVE' | 'INACTIVE';

export interface WarehouseSku {
  id: string;
  sku: string;
  itemName: string;
  uomName: string;
  brand?: string | null;
  productCategory?: string | null;
  productType?: string | null;
  barcode?: string | null;
  weightKg?: number | null;
  status?: SkuStatus;
  createdAt: string;
}

/* ------------------------------- outbound ----------------------------- */

export const OUTBOUND_STATUSES = [
  'OPEN',
  'PREPARING',
  'READY_TO_PICKUP',
  'SHIPPED',
  'IN_DELIVERY',
  'DELIVERED',
  'FAILED',
  'CANCELLED',
] as const;
export type OutboundOrderStatus = (typeof OUTBOUND_STATUSES)[number];

export type FulfillmentStatus =
  | 'AT_HUB'
  | 'DISPATCHED'
  | 'IN_DELIVERY'
  | 'DELIVERED'
  | 'FAILED'
  | 'RETURNED'
  | 'CANCELLED';

export interface OutboundOrderFulfillment {
  carrier?: string | null;
  waybill?: string | null;
  shipmentID?: string | null;
  status?: FulfillmentStatus | null;
}

/** The rider carrying a shipped order (Dash shipments only) — like `hub`, a
 *  snapshot with its id beside it on the order. */
export interface Courier {
  id: number;
  /** Mitra code, e.g. MTR0006903. */
  code: string;
  name: string;
  phone: string | null;
  uuid: string | null;
  profileImageUrl: string | null;
}

export interface OutboundOrder {
  id: string;
  code: string;
  source: 'MANUAL' | 'CSV' | 'CLIENT_API';
  externalID: string;
  hubID: number;
  hub: HubRef | null;
  clientID: number;
  type: 'REGULAR' | 'TRANSFER';
  destinationHub: HubRef | null;
  status: OutboundOrderStatus;
  serviceType: string;
  method: string;
  recipientName: string;
  recipientPhone: string;
  destinationAddress: string;
  destinationLatitude: number;
  destinationLongitude: number;
  destinationNotes: string;
  /** Destination city as Dash resolved it (core's city master, from the
   *  address, else the point); both null while unresolved. */
  destinationCity: string | null;
  destinationCityID: number | null;
  notes: string;
  orderDate: string;
  /** YYYY-MM-DD */
  expectedDeliveryDate: string;
  lineCount: number;
  totalQty: number;
  fulfillment: OutboundOrderFulfillment | null;
  /** null until a rider is assigned; always null for a third-party carrier. */
  courierID: number | null;
  courier: Courier | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  createdAt: string;
}

export interface OutboundOrderLine {
  id: string;
  sku: string;
  itemName: string;
  uomName: string;
  qty: number;
  qtyReserved: number;
  qtyPicked: number;
}

export interface OutboundOrderEvent {
  id: string;
  status: OutboundOrderStatus | null;
  title: string;
  note: string;
  occurredAt: string;
}

export interface OutboundOrderDetail {
  order: OutboundOrder;
  lines: OutboundOrderLine[];
  /** Newest first. */
  events: OutboundOrderEvent[];
}

/* -------------------------- purchase / inbound ------------------------ */

export const PO_STATUSES = ['DRAFT', 'CONFIRMED', 'CANCELLED'] as const;
export type PurchaseOrderStatus = (typeof PO_STATUSES)[number];

export const RECEIPT_STATUSES = [
  'SCHEDULED',
  'PARTIAL_RECEIPT',
  'FINISHED',
] as const;
export type PurchaseOrderReceiptStatus = (typeof RECEIPT_STATUSES)[number];

export type PurchaseOrderType = 'REGULAR' | 'TRANSFER' | 'RETURN';

export interface PurchaseOrder {
  id: string;
  code: string;
  hubID: number;
  hub: HubRef | null;
  clientID: number;
  type?: PurchaseOrderType;
  vendor: (HubRef & { code?: string | null }) | null;
  sourceHub: HubRef | null;
  /** Display name of the source for every type. */
  supplierName: string;
  estArrivalAt: string | null;
  notes: string;
  status: PurchaseOrderStatus;
  /** Inbound progress; null while DRAFT / CANCELLED. */
  receiptStatus: PurchaseOrderReceiptStatus | null;
  lineCount: number;
  totalQty: number;
  totalReceived: number;
  confirmedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  createdAt: string;
}

export interface PurchaseOrderLine {
  id: string;
  sku: string;
  itemName: string;
  uomName: string;
  qty: number;
  qtyReceived: number;
  qtyRemaining: number;
}

export interface GoodsReceiptSummary {
  id: string;
  code: string;
  status: 'DRAFT' | 'POSTED';
  arrivedAt: string | null;
  deliveryNoteNo: string | null;
  lineCount: number;
  totalQty: number;
  postedAt: string | null;
  createdAt: string;
}

export interface PurchaseOrderDetail {
  order: PurchaseOrder;
  lines: PurchaseOrderLine[];
  goodsReceipts: GoodsReceiptSummary[];
}

/* -------------------------------- calls ------------------------------- */

/** A list's applied "Filter" values, already in API param form
 *  (`dateFrom`, `type`, …) — see WarehouseFilter. */
export type FilterParams = Record<string, string>;

export interface ListQuery {
  search?: string;
  page: number;
  limit: number;
}

const get = async <T>(
  path: string,
  params?: Record<string, unknown>
): Promise<T> => {
  const res = await Axios.get(`${BASE}${path}`, {
    params,
    headers: CHANNEL_HEADERS_LIVE,
  });
  return res.data.data as T;
};

/** Drops empty filters so the query string stays clean. */
const clean = (q: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(q).filter(
      ([, v]) => v !== undefined && v !== null && v !== ''
    )
  );

/* ------------------------------ CSV export ---------------------------- */

/** `filename="outbound-request-27102026.csv"` → the server's own file name. */
const filenameOf = (disposition: unknown, fallback: string): string => {
  const match =
    typeof disposition === 'string'
      ? /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
      : null;
  return match ? decodeURIComponent(match[1]) : fallback;
};

/** The backend's `{ error }` message of a failed blob request, else null. */
const blobErrorMessage = async (error: unknown): Promise<string | null> => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!(data instanceof Blob)) return null;
  try {
    const parsed = JSON.parse(await data.text());
    const message = parsed?.error ?? parsed?.message;
    return typeof message === 'string' && message.trim() ? message : null;
  } catch {
    return null;
  }
};

/**
 * GETs a server-built CSV and saves it under the server's file name. A 4xx
 * (e.g. 422 EXPORT_TOO_LARGE) arrives as a Blob too, so its JSON body is read
 * for the backend's message before rethrowing.
 */
const downloadCsv = async (
  path: string,
  params: Record<string, unknown>,
  fallbackName: string
): Promise<void> => {
  try {
    const res = await Axios.get(`${BASE}${path}`, {
      params: clean(params),
      headers: CHANNEL_HEADERS_LIVE,
      responseType: 'blob',
    });
    const url = URL.createObjectURL(
      new Blob([res.data], { type: 'text/csv;charset=utf-8' })
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = filenameOf(res.headers?.['content-disposition'], fallbackName);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    throw new Error(
      (await blobErrorMessage(error)) ?? getBackendErrorMessage(error)
    );
  }
};

/** YYYY-MM-DD, inclusive both ends. */
export interface PeriodQuery {
  dateFrom: string;
  dateTo: string;
}

export const warehouseExport = {
  /** Inventory: the stock table's rows (per batch), every hub. */
  stocks: (q: { search?: string }, f: FilterParams = {}) =>
    downloadCsv('/stocks/export', { ...f, ...q }, 'inventory.csv'),
  /** Laporan Stock: every in / out / transfer in the period. */
  stockMovements: (q: PeriodQuery) =>
    downloadCsv('/stock-movements/export', { ...q }, 'Laporan Stock.csv'),
  /** Laporan Mutasi: opening / in / out / closing per SKU per day. */
  stockMutations: (q: PeriodQuery) =>
    downloadCsv('/stock-mutations/export', { ...q }, 'Laporan Mutasi.csv'),
  outboundOrders: (
    q: { search?: string; status?: OutboundOrderStatus },
    f: FilterParams = {}
  ) =>
    downloadCsv(
      '/outbound-orders/export',
      { ...f, ...q },
      'outbound-request.csv'
    ),
  purchaseOrders: (
    q: { search?: string; status?: PurchaseOrderStatus },
    f: FilterParams = {}
  ) =>
    downloadCsv(
      '/purchase-orders/export',
      { ...f, ...q },
      'purchase-order.csv'
    ),
  inboundRequests: (
    q: { search?: string; receiptStatus?: PurchaseOrderReceiptStatus },
    f: FilterParams = {}
  ) =>
    downloadCsv(
      '/inbound-requests/export',
      { ...f, ...q },
      'inbound-request.csv'
    ),
  skus: (q: { search?: string }, f: FilterParams = {}) =>
    downloadCsv('/skus/export', { ...f, ...q }, 'sku.csv'),
};

export const warehouseApi = {
  listStocks: (q: ListQuery, f: FilterParams = {}) =>
    get<Paged<WarehouseStock>>('/stocks', clean({ ...f, ...q })),
  /** `locationType: 'QUARANTINE'` → totals of stock held in quarantine
   *  locations only (the Stock quarantine tile). */
  getStockSummary: (
    q: { search?: string; locationType?: 'QUARANTINE' },
    f: FilterParams = {}
  ) => get<StockSummary>('/stocks/summary', clean({ ...f, ...q })),

  /** Newest first, every hub; `limit` max 500. */
  listMovements: (q: { limit: number }) =>
    get<StockMovement[]>('/movements', clean({ ...q })),

  listSkus: (q: ListQuery, f: FilterParams = {}) =>
    get<Paged<WarehouseSku>>('/skus', clean({ ...f, ...q })),

  listOutboundOrders: (
    q: ListQuery & { status?: OutboundOrderStatus },
    f: FilterParams = {}
  ) => get<Paged<OutboundOrder>>('/outbound-orders', clean({ ...f, ...q })),
  getOutboundSummary: (q: { search?: string }, f: FilterParams = {}) =>
    get<StatusCount<OutboundOrderStatus>>(
      '/outbound-orders/summary',
      clean({ ...f, ...q })
    ),
  getOutboundOrder: (id: string) =>
    get<OutboundOrderDetail>(`/outbound-orders/${id}`),

  listPurchaseOrders: (
    q: ListQuery & { status?: PurchaseOrderStatus },
    f: FilterParams = {}
  ) => get<Paged<PurchaseOrder>>('/purchase-orders', clean({ ...f, ...q })),
  getPurchaseOrderSummary: (q: { search?: string }, f: FilterParams = {}) =>
    get<StatusCount<PurchaseOrderStatus>>(
      '/purchase-orders/summary',
      clean({ ...f, ...q })
    ),
  getPurchaseOrder: (id: string) =>
    get<PurchaseOrderDetail>(`/purchase-orders/${id}`),

  listInboundRequests: (
    q: ListQuery & { receiptStatus?: PurchaseOrderReceiptStatus },
    f: FilterParams = {}
  ) => get<Paged<PurchaseOrder>>('/inbound-requests', clean({ ...f, ...q })),
  getInboundRequestSummary: (q: { search?: string }, f: FilterParams = {}) =>
    get<StatusCount<PurchaseOrderReceiptStatus>>(
      '/inbound-requests/summary',
      clean({ ...f, ...q })
    ),
  getInboundRequest: (id: string) =>
    get<PurchaseOrderDetail>(`/inbound-requests/${id}`),
};
